import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { TICKS_KEY, isExpired } from "@/lib/offlineStore";

// Offline visit check-in: cached visit snapshot (expires after 24 h) + queued ticks (no credentials; auth is the httpOnly cookie).
const QKEY = TICKS_KEY;
const cacheKey = (id) => `qs_visit_cache_${id}`;
const read = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const write = (q) => localStorage.setItem(QKEY, JSON.stringify(q));
export const readVisitCache = (id) => {
  try {
    const v = JSON.parse(localStorage.getItem(cacheKey(id)) || "null");
    if (v && isExpired(v.cached_at)) { localStorage.removeItem(cacheKey(id)); return null; }
    return v;
  } catch { return null; }
};
export const writeVisitCache = (id, v) => localStorage.setItem(cacheKey(id), JSON.stringify({ ...v, cached_at: new Date().toISOString() }));

const removeTick = (visit_id, followup_id) => write(read().filter((t) => !(t.visit_id === visit_id && t.followup_id === followup_id)));

export async function syncVisitTicks(visitId, onChange) {
  const mine = () => read().filter((x) => visitId == null || x.visit_id === visitId);
  let synced = 0, failed = 0;
  for (const t of mine()) {
    try {
      await api.post(`/health/cases/${t.case_id}/followups/${t.followup_id}/complete`, { outcome: t.outcome, note: `offline tick ${t.at.slice(0, 16)}` });
      synced += 1; removeTick(t.visit_id, t.followup_id);
    } catch (e) {
      const s = e.response?.status;
      if (s === 409) { synced += 1; removeTick(t.visit_id, t.followup_id); } // already completed on another device
      else if (s && s >= 400 && s < 500 && s !== 401 && s !== 429) { removeTick(t.visit_id, t.followup_id); failed += 1; }
      else { write(read().map((x) => x.visit_id === t.visit_id && x.followup_id === t.followup_id ? { ...x, status: "retry", attempts: (x.attempts || 0) + 1, error: e.response?.data?.detail || e.message } : x)); failed += 1; }
    }
    onChange?.();
  }
  return { synced, failed, pending: mine().length };
}

export function useVisitQueue(visitId) {
  const [queue, setQueue] = useState(() => read().filter((t) => t.visit_id === visitId));
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const refresh = useCallback(() => setQueue(read().filter((t) => t.visit_id === visitId)), [visitId]);

  const enqueue = useCallback((t) => { write([...read(), { ...t, visit_id: visitId, at: new Date().toISOString(), status: "queued", attempts: 0 }]); refresh(); }, [visitId, refresh]);
  const remove = useCallback((followup_id) => { removeTick(visitId, followup_id); refresh(); }, [visitId, refresh]);

  const sync = useCallback(async () => {
    if (!navigator.onLine || syncing) return { synced: 0, failed: 0, pending: read().filter((t) => t.visit_id === visitId).length };
    setSyncing(true);
    const res = await syncVisitTicks(visitId, refresh);
    setSyncing(false); setLastSync(new Date());
    return res;
  }, [visitId, refresh, syncing]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down); window.addEventListener("qs-offline-changed", refresh);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); window.removeEventListener("qs-offline-changed", refresh); };
  }, [refresh]);

  return { queue, online, syncing, lastSync, enqueue, remove, sync };
}
